import H from 'history'
import {
    StaticRoutes,
    RouteServices,
    StaticPageRoute,
    StaticRoute,
} from '../routing/page-definition'
import { resources, DataLoaderGlobalParams } from '../data/resources'
import { getAdTargetingPath } from '../advertising/ad-targeting'
import { ResolveSlugV4DTO } from '@west-australian-newspapers/publication-types'
import { RouteHints } from '../routing/route-hints'
import { resolveSlug } from '../__content-api/v4/resolve-route'
import { stripTrailingSlash, getSiteSection } from './helpers'
import { diagFailResolveRoute } from '../routing/error-routes'
import { RenderTarget } from '../context-providers/render-target-context'
import { AdTargeting } from '../advertising/add-ads'
import { RouteInfoResolverFailedEvent, PageError } from '../events/page-events'
import { GptApi } from '../advertising/gpt-api'

let staticRoutes: StaticRoutes = {}
/** matches route keys as regex */
let matchRoutes: StaticRoutes

let serverRoutes: ServerResolvedRoutes
let errorRouteInfo: StaticPageRoute
let notFoundRouteInfo: StaticPageRoute

export interface QueryString {
    [key: string]: boolean | number | string
}

export interface SsrResolverProps {
    host: string | undefined
    path: string
    query: QueryString
    state: any
    routeServices: RouteServices
}

export function registerPageRoutes<Sections extends string = string>(routes: {
    staticRoutes: StaticRoutes<Sections>
    /** matches route keys as regex */
    matchRoutes: StaticRoutes<Sections>
    serverRoutes: ServerResolvedRoutes<Sections>
    errorRouteInfo: StaticPageRoute<Sections>
    notFoundRouteInfo: StaticPageRoute<Sections>
}) {
    staticRoutes = routes.staticRoutes
    matchRoutes = routes.matchRoutes
    serverRoutes = routes.serverRoutes
    errorRouteInfo = routes.errorRouteInfo
    notFoundRouteInfo = routes.notFoundRouteInfo
}

export function getRegisteredRoutes() {
    return {
        staticRoutes,
        matchRoutes,
        serverRoutes,
        errorRouteInfo,
        notFoundRouteInfo,
    }
}

type ResolveRouteResolution =
    | StaticRouteResolution
    | MatchRouteResolution
    | ServerResolution
    | ErrorResolution
    | Promise<
          | StaticRouteResolution
          | MatchRouteResolution
          | ServerResolution
          | ErrorResolution
      >

function resolveRouteErrorResponse(props: {
    services: DataLoaderGlobalParams
    err: Error
    section: string | undefined
    path: string
    message: string
}): {
    type: 'error'
    error: { errorType: '404' | '500' }
    section: string | undefined
} {
    const { services, err, section, path, message } = props
    if (err.message === '404') {
        return {
            type: 'error',
            error: {
                errorType: '404',
            },
            section,
        }
    }
    services.log.error({ err, path: path }, message)

    return {
        type: 'error',
        error: {
            errorType: '500',
        },
        section,
    }
}

export function resolveRoute(props: ResolveRouteProps): ResolveRouteResolution {
    const { hostname, services } = props
    const originalPath = stripTrailingSlash(props.path)
    const { path, section, isSectionPrepended } = getSitePathSection(
        originalPath,
        hostname,
        services,
    )

    try {
        // Resolve potential diag routes - needs to be against originalPath
        diagFailResolveRoute(originalPath)

        props.services.log.debug({ path }, `Resolving`)

        // First lets see if we have any static routes defined
        const staticRouteResult = staticRoutes[path]
        if (staticRouteResult !== undefined) {
            props.services.log.debug(
                { path },
                `Resolved statically defined route`,
            )

            return { type: 'static', key: path }
        }

        // Then match on routes with regex keys
        const matchRouteKey = Object.keys(matchRoutes).find((key) =>
            new RegExp(key).test(path),
        )

        if (matchRouteKey) {
            props.services.log.debug(
                { path, matchRouteKey },
                `Resolved match route`,
            )
            return { type: 'match', key: matchRouteKey, section }
        }

        const pathWithNoExtension = originalPath.replace('.amp', '')

        return resolveSlug(
            props.services,
            pathWithNoExtension,
            isSectionPrepended ? section : undefined,
        )
            .then<ServerResolution>((resolved) => {
                props.services.log.debug(
                    `Resolved slug ${path}: "${resolved.type}"`,
                )

                return {
                    type: 'server',
                    resolution: resolved,
                }
            })
            .catch((err) => {
                return resolveRouteErrorResponse({
                    services,
                    err,
                    path,
                    section,
                    message: 'Failed to resolve slug',
                })
            })
    } catch (err) {
        return resolveRouteErrorResponse({
            services,
            err,
            path,
            section,
            message: 'Unknown error during route resolution',
        })
    }
}

export interface RouteInfoParams {
    path: string
    host: string | undefined
    location: H.Location
    search: string
    failed: (error: RouteInfoResolverFailedEvent) => void
}

export const useRoute = resources.registerResource<
    RouteResolution,
    ResolveRouteProps
>(
    'route',
    (params) =>
        params.routeCache.getOrLoad(
            params.resourceType,
            params.paramsCacheKey,
            { path: params.path },
            () => resolveRoute(params),
        ),
    ['path'],
)

export function createRouteServices<
    Resolution extends RouteResolution = RouteResolution
>(
    services: DataLoaderGlobalParams,
    resolution: Resolution,
    location: H.Location,
    hostname: string | undefined,
    protocol: string | undefined,
    renderTarget: RenderTarget,
    gptApi: GptApi,
): RouteServices<Resolution> {
    return {
        ...services,
        gptApi,
        getAdTargeting: (
            adPageKind,
            section,
            primaryTopicOrPageId,
            ...secondaryTopics
        ) => {
            const pageId =
                typeof primaryTopicOrPageId === 'string'
                    ? primaryTopicOrPageId
                    : primaryTopicOrPageId.title
            const primaryTopic =
                typeof primaryTopicOrPageId === 'object'
                    ? primaryTopicOrPageId
                    : undefined

            const { adUnitPath, ssAdUnits, topics } = getAdTargetingPath(
                services.config,
                adPageKind,
                section,
                primaryTopic,
                ...secondaryTopics,
            )
            const adTargeting: AdTargeting = {
                pageId,
                adUnitPath,
                ssAdUnits,
                topics,
            }
            return adTargeting
        },
        resolution,
        location,
        hostname,
        protocol,
        renderTarget,
    }
}

export type ServerResolvedRoutes<Sections extends string = string> = {
    [resolvedType in ResolveSlugV4DTO['type']]: StaticRoute<
        Sections,
        ServerResolution<Extract<ResolveSlugV4DTO, { type: resolvedType }>>
    >
}

export interface ResolveRouteProps {
    path: string
    search: string
    hostname: string | undefined
    services: DataLoaderGlobalParams
}

export interface RouteInfoParams {
    failed: (error: RouteInfoResolverFailedEvent) => void
    location: H.Location
    // RouteResolver data loader needs a top level prop to match against
    path: string
    search: string
}

export interface StaticRouteResolution {
    type: 'static'
    key: string
}
export interface MatchRouteResolution {
    type: 'match'
    key: string
    section: string | undefined // undefined if a section is not present in the sectionOverrides
}

export interface ErrorResolution {
    type: 'error'
    error: PageError
    section: string | undefined // undefined if a section is not present in the sectionOverrides
}
export interface ServerResolution<
    Resolved extends ResolveSlugV4DTO = ResolveSlugV4DTO
> {
    type: 'server'
    resolution: Resolved
}

export type RouteResolution =
    | StaticRouteResolution
    | MatchRouteResolution
    | ServerResolution
    | ErrorResolution
    | RouteHintResolution
    | LoadingRouteResolution
    | InspectRoute

/**
 * We have a number of services which 'inspect' routes, this could be for tests, or to find all static content used within the site
 *
 * This resolution can be used in those cases, and all routes should work with it
 **/
export interface InspectRoute {
    type: 'inspect'
}

/** Used to render an initial rendition of certain routes (publication, topic etc) while required data is loading */
export interface RouteHintResolution {
    type: 'route-hint'
    routeHint: RouteHints
}

/** If we are loading and no route hints available */
export interface LoadingRouteResolution {
    type: 'loading'
    section: string | undefined
}

export interface RedirectTo {
    targetUrl: string
    httpStatusCode?: 301 | 302 | 303 | 307 | 308
}

function getSitePathSection(
    path: string,
    hostname: string | undefined,
    services: DataLoaderGlobalParams,
) {
    // First try to resolve via hostname for the case sectionDomains is enabled
    const meta = services.store.getState().meta
    const { resolveType, section } = getSiteSection(
        path,
        hostname,
        meta.hostnameToSectionLookup,
        meta.sectionMeta,
    )

    if (resolveType === 'hostname') {
        return {
            isSectionPrepended: true,
            path: `/${section}${path}`,
            section: section,
        }
    }

    // Resolve via pathname
    return { isSectionPrepended: false, path, section }
}
